import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { getTooltip } from "./tooltips.js";
import { handleUIEvent } from "./eventLogger.js";

const MODULE_NAME = "_45_user_profile";

function Tooltip({ text, screen, tooltipRef }) {
    return (
        <span
            ref={tooltipRef}
            style={{
                marginLeft: 4,
                display: "inline-flex",
                alignItems: "center",   
                verticalAlign: "bottom",
                cursor: "pointer",
                color: "#fff"
            }}
            title={text}
            onClick={e => handleUIEvent(e, { action: "tooltip_click", tooltip: text }, null, screen, MODULE_NAME)}
        >
            🛈
            <span style={{ fontSize: "0.9em", marginLeft: 2, userSelect: "none" }}>▼</span>
        </span>
    );
}

function useIsMobile(breakpoint = 700) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);
    return isMobile;
}

function formatNumberInput(value) {
    // Remove all non-digit characters
    const numbersOnly = value.replace(/\D/g, "");
    
    // Add commas for display
    return numbersOnly.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function _45UserProfile({ screen, activeDate, onDateChange }) {
    const [fname, setFname] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [loading, setLoading] = useState(true);
    const [showLogoutMenu, setShowLogoutMenu] = useState(false);
    const [error, setError] = useState("");
    const [menuPosition, setMenuPosition] = useState({ top: 70, left: 0 });
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelMessage, setCancelMessage] = useState("");
    const [accountType, setAccountType] = useState("");
    const [isExpandedOptions, setIsExpandedOptions] = useState(false);
    const [balanceInput, setBalanceInput] = useState("");
    const [isUpdatingBalance, setIsUpdatingBalance] = useState(false);
    const [balanceMessage, setBalanceMessage] = useState("");
    const [currentBalance, setCurrentBalance] = useState("");
    
    const isMobile = useIsMobile();                                         
    const tooltipRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/api/user-profile`, { withCredentials: true })
            .then(res => {
                setFname(res.data.fname || "");
                setUserEmail(res.data.email || "");
                setTooltip(res.data.tooltip || "");
                if (res.data.account_status !== undefined) {
                    sessionStorage.setItem("account_status", res.data.account_status || "");
                }
                if (res.data.account_type !== undefined) {
                    sessionStorage.setItem("account_type", res.data.account_type || "");
                    setAccountType(res.data.account_type || "");
                }
                setError("");
            })
            .catch(err => {
                setError("Failed to load user profile.");
                handleUIEvent(
                    null,
                    { action: "api_error", error: err.message || err.toString(), endpoint: `${process.env.REACT_APP_API_URL}/api/user-profile` },
                    null,
                    screen,
                    MODULE_NAME
                );
            })
            .finally(() => setLoading(false));
    }, [screen]);

    // Fetch current balance when expanding options
    useEffect(() => {
        if (isExpandedOptions) {
            axios.get(`${process.env.REACT_APP_API_URL}/api/get-balance`, { withCredentials: true })
                .then(res => {
                    setCurrentBalance(formatNumberInput(res.data.acct_balance.toString()));
                    setBalanceMessage("");
                })
                .catch(err => {
                    setBalanceMessage("");
                    handleUIEvent(
                        null,
                        { action: "api_error", error: err.message || err.toString(), endpoint: `${process.env.REACT_APP_API_URL}/api/get-balance` },
                        null,
                        screen,
                        MODULE_NAME
                    );
                });
        }
    }, [isExpandedOptions, screen]);

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setShowLogoutMenu(false);
                handleUIEvent(null, { action: "menu_closed_outside_click" }, null, screen, MODULE_NAME);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape" && showLogoutMenu) {
                setShowLogoutMenu(false);
                handleUIEvent(null, { action: "menu_closed_escape_key" }, null, screen, MODULE_NAME);
            }
        };

        if (showLogoutMenu) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [showLogoutMenu, screen]);

    const handleNameClick = (e) => {
        if (tooltipRef.current) {
            const rect = tooltipRef.current.getBoundingClientRect();
            const menuWidth = 240;
            const left = rect.left + (rect.width / 2) - (menuWidth / 2);
            const top = rect.bottom + 8;
            setMenuPosition({ top, left });
        }
        setShowLogoutMenu(true);
        handleUIEvent(e, { action: "name_click", fname }, null, screen, MODULE_NAME);
    };

    const handleLogout = (e) => {
        handleUIEvent(e, { action: "logout_click" }, null, screen, MODULE_NAME);
        axios.post(`${process.env.REACT_APP_API_URL}/api/logout`, {}, { withCredentials: true })
            .then(() => {
                sessionStorage.removeItem("user_id");
                sessionStorage.removeItem("user_type");
                sessionStorage.removeItem("account_status");
                sessionStorage.removeItem("account_type");
                window.location.href = "https://pro.tontradingtools.com";
            })
            .catch(() => {
                sessionStorage.removeItem("user_id");
                sessionStorage.removeItem("user_type");
                sessionStorage.removeItem("account_status");
                sessionStorage.removeItem("account_type");
                window.location.href = "https://pro.tontradingtools.com";
            });
    };

    const closeMenu = (e) => {
        e?.preventDefault();
        setShowLogoutMenu(false);
        setCancelMessage("");
        setIsExpandedOptions(false);
        handleUIEvent(e, { action: "menu_closed" }, null, screen, MODULE_NAME);
    };

    const handleBalanceInputChange = (e) => {
        const value = e.target.value;
        const formatted = formatNumberInput(value);
        setBalanceInput(formatted);
    };

    const handleUpdateBalance = async (e) => {
        e.preventDefault();
        
        if (!balanceInput) {
            setBalanceMessage("Please enter an amount.");
            return;
        }

        // Remove commas for API submission
        const numbersOnly = balanceInput.replace(/,/g, "");
        const balanceValue = parseFloat(numbersOnly);

        if (isNaN(balanceValue) || balanceValue <= 0) {
            setBalanceMessage("Please enter a valid positive number.");
            return;
        }

        setIsUpdatingBalance(true);
        setBalanceMessage("");

        try {
            handleUIEvent(e, { action: "update_balance_start", amount: balanceValue }, null, screen, MODULE_NAME);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/update-balance`,
                { acct_balance: balanceValue },
                { withCredentials: true }
            );

            if (response.data.status === "success") {
                setBalanceMessage(`✓ Balance updated to $${balanceInput}`);
                setCurrentBalance(balanceInput);
                setBalanceInput("");
                handleUIEvent(null, { action: "update_balance_success", amount: balanceValue, action_type: response.data.action }, null, screen, MODULE_NAME);
            } else {
                setBalanceMessage(`Error: ${response.data.error || "Failed to update balance"}`);
                handleUIEvent(null, { action: "update_balance_failed", error: response.data.error }, null, screen, MODULE_NAME);
            }
        } catch (err) {
            setBalanceMessage(err.response?.data?.error || "System error. Try again later.");
            handleUIEvent(null, { action: "update_balance_error", error: err.message }, null, screen, MODULE_NAME);
        } finally {
            setIsUpdatingBalance(false);
        }
    };

    const handleCancelAccount = async (e) => {
        e.preventDefault();
        
        handleUIEvent(e, { action: "cancel_account_start", email: userEmail }, null, screen, MODULE_NAME);

        if (!window.confirm("Are you sure you want to cancel your account? This action cannot be undone.")) {
            handleUIEvent(e, { action: "cancel_account_aborted", email: userEmail }, null, screen, MODULE_NAME);
            return;
        }

        setIsCancelling(true);
        setCancelMessage("");

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/cancel-account`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include", 
                body: JSON.stringify({ email: userEmail })
            });
            const data = await response.json();

            if (data.success) {
                if (data.confirmation_number === "LOCAL_ONLY") {
                    setCancelMessage(`No payment found for this email address. Please email support @ "support@tontradingtools.com" if you believe this to be incorrect.`);
                } else {
                    setCancelMessage(`Account cancelled. Confirmation #: ${data.confirmation_number || "N/A"}`);
                }
                handleUIEvent(null, { action: "cancel_account_success", confirmation: data.confirmation_number }, null, screen, MODULE_NAME);
            } else {
                setCancelMessage(`Error: ${data.error || "Failed to cancel"}`);
                handleUIEvent(null, { action: "cancel_account_failed", error: data.error }, null, screen, MODULE_NAME);
            }
        } catch (err) {
            setCancelMessage("System error. Try again later.");
        } finally {
            setIsCancelling(false);
        }
    };

    if (loading) return <div>Loading profile...</div>;
    if (error) return <div style={{ color: "red" }}>{error}</div>;

    return (
        <div 
            className="user-profile-container"
            style={{ 
                position: "relative", 
                zIndex: showLogoutMenu ? 10001 : 1 
            }}
        >
            <div>
                Hi:{" "}
                <span
                    style={{ cursor: "pointer", color: "#fff" }}
                    onClick={handleNameClick}
                    title="Click to log out"
                    id="profilename"
                >
                    {fname}
                    <Tooltip text={tooltip} screen={screen} tooltipRef={tooltipRef} />
                </span>
            </div>
            {showLogoutMenu && (
                <div
                    style={{
                        position: "relative",
                        display: "inline-block"
                    }}
                >
                    {/* Close Button - Outside rectangle */}
                    <button
                        onClick={closeMenu}
                        style={{
                            position: "absolute",
                            top: -24,
                            right: 0,
                            background: "transparent",
                            border: "none",
                            color: "#999",
                            fontSize: "1.2rem",
                            cursor: "pointer",
                            padding: "0 4px",
                            lineHeight: 1,
                            zIndex: 10003
                        }}
                    >
                        ✕
                    </button>

                    <div
                        ref={menuRef}
                        style={{
                            position: "absolute",
                            top: menuPosition.top,
                            left: menuPosition.left,
                            width: 240,
                            background: "#2F242C",
                            color: "#fff",
                            borderRadius: 8,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                            padding: 20,
                            zIndex: 10002
                        }}
                    >
                        <div style={{ marginBottom: 16, textAlign: "left" }}>
                            Would you like to log out?
                        </div>
                        <div style={{ display: "flex", gap: 12 }}>
                            <button
                                style={{
                                    padding: "8px 18px",
                                    background: "#B22222",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    fontWeight: "bold",
                                    cursor: "pointer"
                                }}
                                onClick={handleLogout}
                            >
                                Log Out
                            </button>
                            <button
                                style={{
                                    padding: "8px 18px",
                                    background: "#228B22",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    fontWeight: "bold",
                                    cursor: "pointer"
                                }}
                                onClick={closeMenu}
                            >
                                Cancel
                            </button>
                        </div>

                        {/* Account Options Section */}
                        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #4A3B45" }}>
                            <button
                                onClick={() => setIsExpandedOptions(!isExpandedOptions)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    background: "transparent",
                                    border: "none",
                                    color: "#ccc",
                                    fontSize: "0.85rem",
                                    fontWeight: "normal",
                                    cursor: "pointer",
                                    padding: 0,
                                    textAlign: "left"
                                }}
                            >
                                <span style={{ fontSize: "0.9rem" }}>
                                    {isExpandedOptions ? "▼" : "▶"}
                                </span>
                                Account options
                            </button>

                            {isExpandedOptions && (
                                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #4A3B45" }}>
                                    {/* Update Balance Section */}
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: "block", fontSize: "0.75rem", marginBottom: 8, fontWeight: "normal", color: "#ccc" }}>
                                            Update Account Balance(used for risk%)
                                        </label>
                                        {currentBalance && (
                                            <div style={{ fontSize: "0.75rem", marginBottom: 8, color: "#999" }}>
                                                Current: ${currentBalance}
                                            </div>
                                        )}
                                        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                                            <input
                                                type="text"
                                                placeholder="Enter amount"
                                                value={balanceInput}
                                                onChange={handleBalanceInputChange}
                                                disabled={isUpdatingBalance}
                                                style={{
                                                    flex: 1,
                                                    padding: "4px 8px",
                                                    background: "#1a1a1a",
                                                    color: "#fff",
                                                    border: "1px solid #4A3B45",
                                                    borderRadius: 3,
                                                    fontSize: "0.8rem",
                                                    opacity: isUpdatingBalance ? 0.5 : 1,
                                                    cursor: isUpdatingBalance ? "not-allowed" : "text"
                                                }}
                                            />
                                            <button
                                                onClick={handleUpdateBalance}
                                                disabled={isUpdatingBalance}
                                                style={{
                                                    padding: "4px 12px",
                                                    background: "#228B22",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: 3,
                                                    fontWeight: "bold",
                                                    fontSize: "0.8rem",
                                                    cursor: isUpdatingBalance ? "not-allowed" : "pointer",
                                                    opacity: isUpdatingBalance ? 0.6 : 1,
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {isUpdatingBalance ? "..." : "Update"}
                                            </button>
                                        </div>
                                        {balanceMessage && (
                                            <p style={{
                                                margin: 0,
                                                fontSize: "0.75rem",
                                                color: balanceMessage.startsWith("✓") ? "#52c41a" : balanceMessage.startsWith("Error") ? "#ff4d4f" : "#ff9d4d",
                                                fontWeight: "normal"
                                            }}>
                                                {balanceMessage}
                                            </p>
                                        )}
                                    </div>

                                    {/* Cancel Account Section */}
                                    {accountType !== "demo" && (
                                        <div style={{ paddingTop: 12, borderTop: "1px solid #4A3B45" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <a 
                                                    href="#" 
                                                    onClick={handleCancelAccount}
                                                    style={{ 
                                                        color: "#ff4d4f", 
                                                        fontSize: "0.8rem", 
                                                        textDecoration: "none",
                                                        pointerEvents: isCancelling ? "none" : "auto"
                                                    }}
                                                >
                                                    Cancel Account
                                                </a>
                                                {isCancelling && (
                                                    <div className="spinner-mini" style={{
                                                        width: 10, height: 10,
                                                        border: "1.5px solid #4A3B45",
                                                        borderTop: "1.5px solid #ff4d4f",
                                                        borderRadius: "50%",
                                                        animation: "spin 1s linear infinite"
                                                    }} />
                                                )}
                                            </div>
                                            {cancelMessage && (
                                                <p style={{ 
                                                    margin: "6px 0 0 0", 
                                                    fontSize: "0.7rem",
                                                    color: cancelMessage.startsWith("Error") ? "#ff4d4f" : "#52c41a",
                                                    fontWeight: "normal"
                                                }}>
                                                    {cancelMessage}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
}

export default _45UserProfile;